import { AiOutlineArrowRight } from "react-icons/ai";
import { BsShieldLock } from "react-icons/bs";
import { MdOutlineDashboardCustomize } from "react-icons/md";
import { Link } from "react-router-dom";
import getPermissions from "../../utils/getPermissions";
import { sections } from "./setupSections";

const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
};

export default function Setup() {
  const permissions = getPermissions() || [];
  const available = sections.filter((s) => permissions.includes(s.permission));
  const userName =
    localStorage.getItem("user") || localStorage.getItem("fullName") || "Admin";
  const today = new Date().toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  return (
    <div className='max-w-6xl mx-auto py-6 md:py-8'>
      {/* Hero */}
      <div className='relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#011f44] via-[#023f88] to-[#0b5cae] px-6 py-8 md:px-10 md:py-12 text-white shadow-xl'>
        <div className='absolute -top-16 -right-16 h-56 w-56 rounded-full bg-white/10 blur-2xl' />
        <div className='absolute -bottom-20 left-1/3 h-48 w-48 rounded-full bg-sky-300/20 blur-2xl' />
        <div className='absolute top-6 right-8 hidden md:block opacity-20'>
          <MdOutlineDashboardCustomize className='text-[140px]' />
        </div>

        <div className='relative'>
          <span className='inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs font-medium tracking-wide uppercase backdrop-blur-sm'>
            <span className='h-2 w-2 rounded-full bg-sky-300 animate-pulse' />
            Admin Control Center
          </span>
          <h1 className='mt-4 text-3xl md:text-5xl font-extrabold tracking-tight text-white'>
            Campus Pulse
          </h1>
          <p className='mt-3 text-base md:text-lg text-blue-50/90 max-w-2xl'>
            {getGreeting()}, <span className='font-semibold'>{userName}</span>.
            Configure your users, roles and content settings from one place.
          </p>
          <p className='mt-2 text-sm text-blue-100/80'>{today}</p>

          <div className='mt-6 flex flex-wrap gap-3'>
            <div className='rounded-xl bg-white/15 px-4 py-3 backdrop-blur-sm'>
              <p className='text-2xl font-bold leading-none'>{available.length}</p>
              <p className='mt-1 text-xs text-blue-50/80'>Modules available</p>
            </div>
            <div className='rounded-xl bg-white/15 px-4 py-3 backdrop-blur-sm'>
              <p className='text-2xl font-bold leading-none'>{permissions.length}</p>
              <p className='mt-1 text-xs text-blue-50/80'>Permissions granted</p>
            </div>
          </div>
        </div>
      </div>

      {/* Modules */}
      <div className='mt-10 flex items-end justify-between'>
        <div>
          <h2 className='text-xl md:text-2xl font-bold text-slate-800 dark:text-white'>
            Setup modules
          </h2>
          <p className='text-sm text-slate-500 dark:text-slate-400'>
            Pick a module to start configuring.
          </p>
        </div>
      </div>

      {available.length ? (
        <div className='mt-5 grid gap-5 sm:grid-cols-2 xl:grid-cols-3'>
          {available.map(({ title, description, to, icon: Icon, accent, group }) => (
            <Link
              key={to}
              to={to}
              className='group relative flex flex-col rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-transparent hover:shadow-xl dark:border-slate-700 dark:bg-slate-800'
            >
              <div className='flex items-start justify-between'>
                <div
                  className={`flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br ${accent} text-white shadow-md transition-transform duration-300 group-hover:scale-110`}
                >
                  <Icon className='text-2xl' />
                </div>
                <span className='rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-medium text-slate-500 dark:bg-slate-700 dark:text-slate-300'>
                  {group}
                </span>
              </div>
              <h3 className='mt-5 text-lg font-semibold text-slate-800 dark:text-white'>
                {title}
              </h3>
              <p className='mt-1.5 flex-1 text-sm leading-relaxed text-slate-500 dark:text-slate-400'>
                {description}
              </p>
              <span className='mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[#0b5cae] dark:text-blue-300'>
                Open
                <AiOutlineArrowRight className='transition-transform duration-300 group-hover:translate-x-1' />
              </span>
            </Link>
          ))}
        </div>
      ) : (
        <div className='mt-5 rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center dark:border-slate-600 dark:bg-slate-800'>
          <BsShieldLock className='mx-auto text-4xl text-slate-400' />
          <p className='mt-3 font-semibold text-slate-700 dark:text-white'>
            No setup modules available
          </p>
          <p className='mt-1 text-sm text-slate-500 dark:text-slate-400'>
            Ask an administrator to grant you setup permissions.
          </p>
        </div>
      )}
    </div>
  );
}
